
import React from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ResellerPlanDetails } from '@/hooks/license/resellerPlans/types';

interface LicenseDefaultSettingsProps {
  duration: string;
  autoRenewal: boolean;
  planId?: string;
  resellerPlans: ResellerPlanDetails[];
  selectedPlan: ResellerPlanDetails | null;
  onSettingChange: (field: string, value: string | boolean | number) => void;
  onPlanChange: (planId: string) => void;
}

const LicenseDefaultSettings: React.FC<LicenseDefaultSettingsProps> = ({
  duration,
  autoRenewal,
  planId,
  resellerPlans,
  selectedPlan,
  onSettingChange,
  onPlanChange
}) => {
  // Handler for duration change
  const handleDurationChange = (value: string) => {
    onSettingChange('duration', value);
  };
  
  // Handler for auto renewal toggle
  const handleAutoRenewalChange = (checked: boolean) => {
    onSettingChange('autoRenewal', checked);
  };
  
  // Handler for plan selection
  const handlePlanChange = (value: string) => {
    onPlanChange(value);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">License Default Settings</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="select-plan">Default Plan Type</Label>
            <Select value={planId} onValueChange={handlePlanChange}>
              <SelectTrigger id="select-plan" className="mt-1">
                <SelectValue placeholder="Select plan" />
              </SelectTrigger>
              <SelectContent>
                {resellerPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id || ''}>
                    {plan.name} (${plan.annualPrice || 999}/yr)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedPlan && (
              <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                <p><span className="font-semibold">Annual Price:</span> ${selectedPlan.annualPrice || 999}</p>
                <p><span className="font-semibold">Setup Fee:</span> ${selectedPlan.setupFee}</p>
              </div>
            )}
          </div>
          
          <div>
            <Label htmlFor="duration">License Duration</Label>
            <Select value={duration} onValueChange={handleDurationChange}>
              <SelectTrigger id="duration" className="mt-1">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 Days</SelectItem>
                <SelectItem value="90">90 Days</SelectItem>
                <SelectItem value="180">6 Months</SelectItem>
                <SelectItem value="365">1 Year</SelectItem>
                <SelectItem value="730">2 Years</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Switch
            id="auto-renewal"
            checked={autoRenewal}
            onCheckedChange={handleAutoRenewalChange}
          />
          <Label htmlFor="auto-renewal">Enable Auto-Renewal by Default</Label>
        </div>
      </div>
    </div>
  );
};

export default LicenseDefaultSettings;
